import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTable,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineShippingReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    RequisitionID: "",
    TrackingNumber: "",
    Status: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [shippingData, setShippingData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("day");

  // ดึงข้อมูลการขนส่ง
  const fetchShippingReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineShippingSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          RequisitionID: filterData.RequisitionID || "",
          TrackingNumber: filterData.TrackingNumber || "",
          Status: filterData.Status || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setShippingData(response.summary || []);
      } else {
        setShippingData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching shipping report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const handleGroupByChange = (e) => {
    setGroupBy(e.target.value);
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เลขที่การขนส่ง</span>
        </HStack>
      ),
      selector: (row) => row.ShipmentID,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เลขที่ใบเบิก</span>
        </HStack>
      ),
      selector: (row) => row.RequisitionID || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ข้อมูลยานพาหนะ</span>
        </HStack>
      ),
      selector: (row) => row.VehicleInfo || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ข้อมูลผู้ขับ</span>
        </HStack>
      ),
      selector: (row) => row.DriverInfo || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เส้นทาง</span>
        </HStack>
      ),
      selector: (row) => row.RouteInfo || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อุณหภูมิเริ่มต้น (°C)</span>
        </HStack>
      ),
      selector: (row) => row.DepartureTemp?.toFixed(2) || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อุณหภูมิระหว่างขนส่ง (°C)</span>
        </HStack>
      ),
      selector: (row) => row.TransportTemp?.toFixed(2) || "-",
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ขนส่ง</span>
        </HStack>
      ),
      selector: (row) => new Date(row.ShipmentDate).toLocaleString("th-TH"),
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หมายเหตุ</span>
        </HStack>
      ),
      selector: (row) => row.Remark || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>สถานะ</span>
        </HStack>
      ),
      selector: (row) => row.Status,
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = shippingData.map((item) => ({
      เลขที่การขนส่ง: item.ShipmentID,
      เลขที่ใบเบิก: item.RequisitionID || "-",
      ข้อมูลยานพาหนะ: item.VehicleInfo || "-",
      ข้อมูลผู้ขับ: item.DriverInfo || "-",
      เส้นทาง: item.RouteInfo || "-",
      "อุณหภูมิเริ่มต้น (°C)": item.DepartureTemp?.toFixed(2) || "-",
      "อุณหภูมิระหว่างขนส่ง (°C)": item.TransportTemp?.toFixed(2) || "-",
      ไฟล์บันทึกอุณหภูมิ: item.TempLogFilePath,
      วันที่ขนส่ง: new Date(item.ShipmentDate).toLocaleString("th-TH"),
      หมายเลขติดตาม: item.TrackingNumber || "-",
      หมายเหตุ: item.Remark || "-",
      สถานะ: item.Status,
      วันที่สร้าง: new Date(item.CreatedAt).toLocaleString("th-TH"),
      ผู้สร้าง: item.CreateBy || "-",
      วันที่อัปเดต: new Date(item.UpdatedAt).toLocaleString("th-TH"),
      ผู้อัปเดต: item.UpdatedBy || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ShippingReport");
    XLSX.writeFile(wb, "VaccineShippingReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการขนส่งวัคซีน", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "เลขที่การขนส่ง",
          "เลขที่ใบเบิก",
          "ข้อมูลยานพาหนะ",
          "ข้อมูลผู้ขับ",
          "เส้นทาง",
          "อุณหภูมิเริ่มต้น (°C)",
          "อุณหภูมิระหว่างขนส่ง (°C)",
          "ไฟล์บันทึกอุณหภูมิ",
          "วันที่ขนส่ง",
          "หมายเลขติดตาม",
          "หมายเหตุ",
          "สถานะ",
          "วันที่สร้าง",
          "ผู้สร้าง",
          "วันที่อัปเดต",
          "ผู้อัปเดต",
        ],
      ],
      body: shippingData.map((item) => [
        item.ShipmentID,
        item.RequisitionID || "-",
        item.VehicleInfo || "-",
        item.DriverInfo || "-",
        item.RouteInfo || "-",
        item.DepartureTemp?.toFixed(2) || "-",
        item.TransportTemp?.toFixed(2) || "-",
        item.TempLogFilePath,
        new Date(item.ShipmentDate).toLocaleString("th-TH"),
        item.TrackingNumber || "-",
        item.Remark || "-",
        item.Status,
        new Date(item.CreatedAt).toLocaleString("th-TH"),
        item.CreateBy || "-",
        new Date(item.UpdatedAt).toLocaleString("th-TH"),
        item.UpdatedBy || "-",
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 30 },
        8: { cellWidth: 30 },
        9: { cellWidth: 20 },
        10: { cellWidth: 30 },
        11: { cellWidth: 20 },
        12: { cellWidth: 30 },
        13: { cellWidth: 20 },
        14: { cellWidth: 30 },
        15: { cellWidth: 20 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineShippingReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Shipping Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Shipping Report</h2>
          <table>
            <thead>
              <tr>
                <th>เลขที่การขนส่ง</th>
                <th>เลขที่ใบเบิก</th>
                <th>ข้อมูลยานพาหนะ</th>
                <th>ข้อมูลผู้ขับ</th>
                <th>เส้นทาง</th>
                <th>อุณหภูมิเริ่มต้น (°C)</th>
                <th>อุณหภูมิระหว่างขนส่ง (°C)</th>
                <th>ไฟล์บันทึกอุณหภูมิ</th>
                <th>วันที่ขนส่ง</th>
                <th>หมายเลขติดตาม</th>
                <th>หมายเหตุ</th>
                <th>สถานะ</th>
                <th>วันที่สร้าง</th>
                <th>ผู้สร้าง</th>
                <th>วันที่อัปเดต</th>
                <th>ผู้อัปเดต</th>
              </tr>
            </thead>
            <tbody>
              ${shippingData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.ShipmentID}</td>
                      <td>${item.RequisitionID || "-"}</td>
                      <td>${item.VehicleInfo || "-"}</td>
                      <td>${item.DriverInfo || "-"}</td>
                      <td>${item.RouteInfo || "-"}</td>
                      <td>${item.DepartureTemp?.toFixed(2) || "-"}</td>
                      <td>${item.TransportTemp?.toFixed(2) || "-"}</td>
                      <td>${item.TempLogFilePath}</td>
                      <td>${new Date(item.ShipmentDate).toLocaleString(
                        "th-TH"
                      )}</td>
                      <td>${item.TrackingNumber || "-"}</td>
                      <td>${item.Remark || "-"}</td>
                      <td>${item.Status}</td>
                      <td>${new Date(item.CreatedAt).toLocaleString(
                        "th-TH"
                      )}</td>
                      <td>${item.CreateBy || "-"}</td>
                      <td>${new Date(item.UpdatedAt).toLocaleString(
                        "th-TH"
                      )}</td>
                      <td>${item.UpdatedBy || "-"}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    const statuses = [
      ...new Set(shippingData.map((item) => item.Status)),
    ].filter(Boolean);
    const dates = [
      ...new Set(
        shippingData.map((item) => {
          const date = new Date(item.ShipmentDate);
          return groupBy === "year"
            ? date.getFullYear().toString()
            : groupBy === "month"
            ? `${date.getFullYear()}-${date.getMonth() + 1}`
            : date.toISOString().split("T")[0];
        })
      ),
    ].sort();

    return dates.map((date) => {
      const dataPoint = { name: date };
      statuses.forEach((status) => {
        const count = shippingData.filter((item) => {
          const itemDate = new Date(item.ShipmentDate);
          const itemDateStr =
            groupBy === "year"
              ? itemDate.getFullYear().toString()
              : groupBy === "month"
              ? `${itemDate.getFullYear()}-${itemDate.getMonth() + 1}`
              : itemDate.toISOString().split("T")[0];
          return itemDateStr === date && item.Status === status;
        }).length;
        dataPoint[status] = count;
      });
      return dataPoint;
    });
  };

  const colors = [
    "#16A085", // Green
    "#3498DB", // Blue
    "#E74C3C", // Red
    "#F1C40F", // Yellow
    "#9B59B6", // Purple
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>วันที่:</strong> {label}
          </p>
          {payload.map((entry) => (
            <p key={entry.name}>
              <strong>{entry.name}:</strong> {entry.value} การขนส่ง
            </p>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการขนส่งวัคซีน" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการขนส่งวัคซีน"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                เลขที่การขนส่ง
              </FormLabel>
              <Input
                type="text"
                name="RequisitionID"
                value={filterData.RequisitionID}
                onChange={handleInputChange}
                placeholder="ระบุเลขที่ใบเบิก (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ทะเบียนรถขนส่ง
              </FormLabel>
              <Input
                type="text"
                name="TrackingNumber"
                value={filterData.TrackingNumber}
                onChange={handleInputChange}
                placeholder="ระบุหมายเลขติดตาม (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                สถานะ
              </FormLabel>
              <Select
                name="Status"
                value={filterData.Status}
                onChange={handleInputChange}
                placeholder="เลือกสถานะ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="InTransit">อยู่ระหว่างขนส่ง</option>
                <option value="Delivered">จัดส่งแล้ว</option>
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    RequisitionID: "",
                    TrackingNumber: "",
                    Status: "",
                  })
                }
              />
              <Search onClick={fetchShippingReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {shippingData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการการขนส่งวัคซีน"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={shippingData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {shippingData.length > 0 && (
        <Box
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={5}
          mt={6}
          bg="white"
        >
          <StandardHeader
            title="กราฟการขนส่งวัคซีนตามสถานะ"
            bgColor="iGreen.500"
            icon={faChartBar}
          />
          <FormControl m={5} maxW="300px">
            <FormLabel fontSize="xl" color="gray">
              กลุ่มตาม
            </FormLabel>
            <Select
              value={groupBy}
              onChange={handleGroupByChange}
              colorScheme="iBlue"
            >
              <option value="day">วัน</option>
              <option value="month">เดือน</option>
              <option value="year">ปี</option>
            </Select>
          </FormControl>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={chartData()}
              margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={150}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={{
                  value: "จำนวนการขนส่ง",
                  angle: -90,
                  position: "insideLeft",
                  offset: -5,
                }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="right"
                wrapperStyle={{ paddingBottom: 10 }}
              />
              {[...new Set(shippingData.map((item) => item.Status))].map(
                (status, index) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    name={
                      status === "InTransit" ? "อยู่ระหว่างขนส่ง" : "จัดส่งแล้ว"
                    }
                    fill={colors[index % colors.length]}
                    barSize={
                      50 /
                      [...new Set(shippingData.map((item) => item.Status))]
                        .length
                    }
                  />
                )
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default VaccineShippingReport;
